// Coach's "Suggestions Management" page, opened from the button at the top
// right of the Wall of Fame. It lists only the logged-in coach's own
// suggestions, in every status, with what happened to each one: who reviewed
// it and when, the admin's note, and the Wall of Fame pin it became (with its
// views, likes and loves). Read-only: reviewing stays on the admin dashboard.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Archive,
  ArrowLeft,
  Bookmark,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Clock,
  ExternalLink,
  Eye,
  FileText,
  Heart,
  Hourglass,
  Image as ImageIcon,
  Link2,
  Loader2,
  MessageSquare,
  Mic,
  Pin,
  RefreshCw,
  Search,
  ThumbsUp,
  UserRound,
  Video,
  X,
  XCircle,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog.jsx";
import { getMyCoachSuggestions } from "../../api";
import { SubmissionMediaPreview, SubmissionThumb } from "./SubmissionMedia";
import "./CoachSuggestionsManagement.css";

// Status labels are written for the coach; the API keeps the stored values.
export const SUGGESTION_STATUS = {
  pending: {
    label: "Waiting for review",
    chip: "bg-amber-50 text-amber-800 border-amber-200",
    icon: Hourglass,
  },
  reviewed: {
    label: "Reviewed",
    chip: "bg-sky-50 text-sky-800 border-sky-200",
    icon: Eye,
  },
  considered: {
    label: "Kept for later",
    chip: "bg-violet-50 text-violet-800 border-violet-200",
    icon: Bookmark,
  },
  approved: {
    label: "Pinned to Wall",
    chip: "bg-emerald-50 text-emerald-800 border-emerald-200",
    icon: Pin,
  },
  rejected: {
    label: "Not selected",
    chip: "bg-rose-50 text-rose-800 border-rose-200",
    icon: XCircle,
  },
  archived: {
    label: "Archived",
    chip: "bg-slate-100 text-slate-700 border-slate-200",
    icon: Archive,
  },
};

const FILTERS = [
  "all",
  "pending",
  "reviewed",
  "considered",
  "approved",
  "rejected",
  "archived",
];

const KIND = {
  image: { label: "Artwork / Photo", icon: ImageIcon },
  video: { label: "Video", icon: Video },
  audio: { label: "Voice Note", icon: Mic },
  text: { label: "Written Work", icon: FileText },
  link: { label: "Project Link", icon: Link2 },
};

const kindOf = (suggestion) =>
  KIND[suggestion.mediaKind || suggestion.kind] || KIND.text;

export const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const capitalize = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const reviewerLabel = (reviewedBy) => {
  if (!reviewedBy?.name) return "";
  const role = reviewedBy.role
    ? ` (${reviewedBy.role.split("-").map(capitalize).join(" ")})`
    : "";
  return `${reviewedBy.name}${role}`;
};

// What the admin did, as one line for the list and the detail timeline.
export const reviewOutcome = (suggestion) => {
  const by = reviewerLabel(suggestion.reviewedBy);
  const on = formatDate(suggestion.reviewedAt);
  const tail = `${by ? ` by ${by}` : ""}${on ? ` on ${on}` : ""}`;
  switch (suggestion.status) {
    case "approved":
      return `Pinned to the Wall of Fame${tail}`;
    case "rejected":
      return `Not selected${tail}`;
    case "archived":
      return `Archived${tail}`;
    case "considered":
      return `Kept for a future Wall of Fame${tail}`;
    case "reviewed":
      return `Reviewed${tail} - no final decision yet`;
    default:
      return "Waiting for an admin to review it";
  }
};

// Where the pin stands now, for approved suggestions only.
export const pinState = (suggestion) => {
  if (suggestion.status !== "approved") return null;
  const { pin } = suggestion;
  if (!pin) {
    return { live: false, text: "No longer on the Wall of Fame" };
  }
  if (pin.isOnWall) {
    return {
      live: true,
      text: pin.expiresAt
        ? `On the Wall until ${formatDate(pin.expiresAt)}`
        : "On the Wall now",
    };
  }
  if (["active", "expired"].includes(pin.status) && pin.expiresAt) {
    return {
      live: false,
      text: `Was on the Wall until ${formatDate(pin.expiresAt)}`,
    };
  }
  return { live: false, text: "Taken off the Wall of Fame" };
};

const StatusChip = ({ status }) => {
  const meta = SUGGESTION_STATUS[status] || SUGGESTION_STATUS.pending;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.chip}`}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {meta.label}
    </span>
  );
};

const Engagement = ({ engagement, large = false }) => {
  const items = [
    { key: "seen", label: "Views", icon: Eye, color: "text-slate-600" },
    { key: "likes", label: "Likes", icon: ThumbsUp, color: "text-orange-500" },
    { key: "loves", label: "Loves", icon: Heart, color: "text-rose-500" },
  ];
  if (large) {
    return (
      <div className="grid grid-cols-3 gap-3">
        {items.map(({ key, label, icon: Icon, color }) => (
          <div
            key={key}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-center"
          >
            <Icon className={`mx-auto h-4 w-4 ${color}`} aria-hidden="true" />
            <div className="mt-1 text-xl font-bold text-slate-800">
              {engagement?.[key] || 0}
            </div>
            <div className="text-xs text-slate-500">{label}</div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <span className="inline-flex items-center gap-3 text-xs font-semibold text-slate-600">
      {items.map(({ key, label, icon: Icon, color }) => (
        <span
          key={key}
          className="inline-flex items-center gap-1"
          title={label}
          aria-label={`${engagement?.[key] || 0} ${label.toLowerCase()}`}
        >
          <Icon className={`h-3.5 w-3.5 ${color}`} aria-hidden="true" />
          {engagement?.[key] || 0}
        </span>
      ))}
    </span>
  );
};

const KindThumb = ({ suggestion }) => {
  const kind = suggestion.mediaKind || suggestion.kind;
  if (kind === "image" || kind === "video") {
    return <SubmissionThumb submission={suggestion} />;
  }
  const { icon: Icon } = kindOf(suggestion);
  return (
    <div className="flex h-14 w-14 flex-none items-center justify-center rounded-md border border-slate-200 bg-slate-50">
      <Icon className="h-6 w-6 text-slate-500" aria-hidden="true" />
    </div>
  );
};

const StatCard = ({ icon: Icon, tone, label, value, hint }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <span
        className={`flex h-10 w-10 flex-none items-center justify-center rounded-xl ${tone}`}
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <div className="text-2xl font-bold leading-tight text-slate-800">
          {value}
        </div>
        <div className="truncate text-sm font-medium text-slate-600">
          {label}
        </div>
      </div>
    </div>
    {hint && <div className="mt-2 text-xs text-slate-500">{hint}</div>}
  </div>
);

const SuggestionContent = ({ suggestion }) => {
  if (suggestion.mediaKind) {
    return (
      <div className="rounded-xl bg-slate-50 p-4 text-center">
        <SubmissionMediaPreview submission={suggestion} />
      </div>
    );
  }
  const content = (suggestion.content || "").trim();
  if (!content) return null;
  if (suggestion.kind === "link" || /^https?:\/\//i.test(content)) {
    return (
      <a
        href={content}
        target="_blank"
        rel="noreferrer"
        className="inline-flex max-w-full items-center gap-2 break-all rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-violet-700 hover:underline"
      >
        <ExternalLink className="h-4 w-4 flex-none" aria-hidden="true" />
        {content}
      </a>
    );
  }
  return (
    <div className="max-h-64 overflow-y-auto whitespace-pre-line rounded-xl border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-700">
      {content}
    </div>
  );
};

const TimelineItem = ({ icon: Icon, tone, title, children }) => (
  <li className="relative flex gap-3 pb-4 last:pb-0">
    <span
      className={`relative z-10 flex h-8 w-8 flex-none items-center justify-center rounded-full ${tone}`}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </span>
    <div className="min-w-0 pt-1">
      <div className="text-sm font-semibold text-slate-800">{title}</div>
      {children && (
        <div className="mt-0.5 text-sm text-slate-600">{children}</div>
      )}
    </div>
  </li>
);

const SuggestionDetails = ({ suggestion, onClose }) => {
  const open = Boolean(suggestion);
  const pin = suggestion ? pinState(suggestion) : null;
  const kind = suggestion ? kindOf(suggestion) : null;
  const decided = suggestion && suggestion.status !== "pending";

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="csm-dialog max-h-[90vh] max-w-3xl gap-0 overflow-y-auto p-0"
        aria-describedby={undefined}
      >
        {suggestion && (
          <div className="p-6">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <DialogTitle className="text-xl font-bold leading-snug text-slate-800">
                  {suggestion.title || "Untitled"}
                </DialogTitle>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <StatusChip status={suggestion.status} />
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                    <kind.icon className="h-3.5 w-3.5" aria-hidden="true" />
                    {kind.label}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close details"
                className="flex h-9 w-9 flex-none items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <SuggestionContent suggestion={suggestion} />

            <dl className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-xl bg-slate-50 px-4 py-3">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Student
                </dt>
                <dd className="mt-0.5 font-semibold text-slate-800">
                  {suggestion.studentName}
                </dd>
              </div>
              <div className="rounded-xl bg-slate-50 px-4 py-3">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Balagruha
                </dt>
                <dd className="mt-0.5 font-semibold text-slate-800">
                  {suggestion.balagruha}
                </dd>
              </div>
              <div className="rounded-xl bg-slate-50 px-4 py-3">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Suggested on
                </dt>
                <dd className="mt-0.5 font-semibold text-slate-800">
                  {formatDate(suggestion.suggestedAt)}
                </dd>
              </div>
            </dl>

            {suggestion.reason && (
              <div className="mt-3 rounded-xl border border-violet-100 bg-violet-50/60 px-4 py-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-violet-700">
                  Your reason
                </div>
                <p className="mt-0.5 text-sm text-slate-700">
                  {suggestion.reason}
                </p>
              </div>
            )}

            <h3 className="mb-3 mt-6 text-sm font-bold uppercase tracking-wide text-slate-500">
              What happened
            </h3>
            <ol className="csm-timeline">
              <TimelineItem
                icon={ClipboardList}
                tone="bg-violet-100 text-violet-700"
                title="You suggested it"
              >
                {formatDate(suggestion.suggestedAt)}
              </TimelineItem>
              {decided ? (
                <TimelineItem
                  icon={
                    suggestion.status === "approved"
                      ? CheckCircle2
                      : SUGGESTION_STATUS[suggestion.status]?.icon || Eye
                  }
                  tone={
                    suggestion.status === "approved"
                      ? "bg-emerald-100 text-emerald-700"
                      : suggestion.status === "rejected"
                        ? "bg-rose-100 text-rose-700"
                        : "bg-sky-100 text-sky-700"
                  }
                  title={reviewOutcome(suggestion)}
                >
                  {suggestion.reviewNotes && (
                    <span className="inline-flex items-start gap-1.5">
                      <MessageSquare
                        className="mt-0.5 h-3.5 w-3.5 flex-none text-slate-400"
                        aria-hidden="true"
                      />
                      <span>Admin note: “{suggestion.reviewNotes}”</span>
                    </span>
                  )}
                </TimelineItem>
              ) : (
                <TimelineItem
                  icon={Clock}
                  tone="bg-amber-100 text-amber-700"
                  title="Waiting for an admin to review it"
                />
              )}
              {pin && (
                <TimelineItem
                  icon={Pin}
                  tone={
                    pin.live
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-200 text-slate-600"
                  }
                  title={pin.text}
                >
                  {suggestion.pin?.pinnedAt &&
                    `Pinned on ${formatDate(suggestion.pin.pinnedAt)}`}
                </TimelineItem>
              )}
            </ol>

            {suggestion.pin && (
              <>
                <h3 className="mb-3 mt-6 text-sm font-bold uppercase tracking-wide text-slate-500">
                  On the Wall of Fame
                </h3>
                <Engagement engagement={suggestion.pin.engagement} large />
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CoachSuggestionsManagement = ({ onBack, autoHeight = false }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const response = await getMyCoachSuggestions();
      if (response?.success) {
        setSuggestions(response.data?.suggestions || []);
        setCounts(response.data?.counts || null);
      } else {
        setError(response?.message || "Couldn't load your suggestions.");
      }
    } catch (err) {
      setError(
        err?.response?.status === 403
          ? "You don't have permission to see coach suggestions."
          : "Couldn't load your suggestions. Please try again.",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const countFor = (key) => {
    if (key === "all") return counts?.total ?? suggestions.length;
    return counts?.[key] ?? suggestions.filter((s) => s.status === key).length;
  };

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return suggestions.filter((s) => {
      if (filter !== "all" && s.status !== filter) return false;
      if (!term) return true;
      return [s.title, s.studentName, s.balagruha, s.reason]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term));
    });
  }, [suggestions, filter, search]);

  const pinnedOnWall = suggestions.filter((s) => s.pin?.isOnWall).length;
  const awaiting =
    countFor("pending") + countFor("reviewed") + countFor("considered");
  const reactions = suggestions.reduce(
    (acc, s) => {
      if (!s.pin) return acc;
      acc.seen += s.pin.engagement?.seen || 0;
      acc.likes += s.pin.engagement?.likes || 0;
      acc.loves += s.pin.engagement?.loves || 0;
      return acc;
    },
    { seen: 0, likes: 0, loves: 0 },
  );

  return (
    <div
      className={`csm w-full ${autoHeight ? "min-h-screen" : "h-screen overflow-y-auto"}`}
      data-testid="coach-suggestions-management"
    >
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <button
              type="button"
              onClick={onBack}
              className="csm-back mt-1 inline-flex flex-none items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Wall of Fame
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-slate-800">
                Suggestions Management
              </h1>
              <p className="mt-0.5 text-sm text-slate-600">
                Student work you suggested for the Wall of Fame, and what the
                admin decided.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => load({ quiet: true })}
            disabled={loading || refreshing}
            className="inline-flex items-center gap-2 rounded-full bg-violet-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
              aria-hidden="true"
            />
            Refresh
          </button>
        </header>

        {loading ? (
          <div
            className="flex flex-col items-center justify-center py-24 text-slate-500"
            role="status"
          >
            <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
            <p className="mt-3 text-sm">Loading your suggestions…</p>
          </div>
        ) : error ? (
          <div
            className="mx-auto max-w-md rounded-2xl border border-rose-200 bg-white p-8 text-center"
            role="alert"
          >
            <AlertCircle className="mx-auto h-10 w-10 text-rose-500" />
            <p className="mt-3 font-semibold text-slate-800">{error}</p>
            <button
              type="button"
              onClick={() => load()}
              className="mt-4 rounded-full bg-violet-600 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-700"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            <section
              className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4"
              aria-label="Summary"
            >
              <StatCard
                icon={ClipboardList}
                tone="bg-violet-100 text-violet-700"
                label="Suggested"
                value={countFor("all")}
                hint="All the work you have suggested"
              />
              <StatCard
                icon={Pin}
                tone="bg-emerald-100 text-emerald-700"
                label="Pinned to Wall"
                value={countFor("approved")}
                hint={`${pinnedOnWall} on the Wall right now`}
              />
              <StatCard
                icon={Hourglass}
                tone="bg-amber-100 text-amber-700"
                label="Awaiting decision"
                value={awaiting}
                hint={`${countFor("pending")} not reviewed yet`}
              />
              <StatCard
                icon={Eye}
                tone="bg-sky-100 text-sky-700"
                label="Views on your pins"
                value={reactions.seen}
                hint={`${reactions.likes} likes · ${reactions.loves} loves`}
              />
            </section>

            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-label="Filter by status"
              >
                {FILTERS.map((key) => {
                  const active = filter === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setFilter(key)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${
                        active
                          ? "border-violet-600 bg-violet-600 text-white"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {key === "all" ? "All" : SUGGESTION_STATUS[key].label}
                      <span
                        className={`rounded-full px-1.5 text-xs ${
                          active
                            ? "bg-white/20 text-white"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {countFor(key)}
                      </span>
                    </button>
                  );
                })}
              </div>
              <label className="csm-search relative block w-full sm:w-64">
                <span className="sr-only">Search suggestions</span>
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search student or title"
                />
              </label>
            </div>

            {visible.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
                <ClipboardList
                  className="mx-auto h-10 w-10 text-slate-300"
                  aria-hidden="true"
                />
                <p className="mt-3 font-semibold text-slate-700">
                  {suggestions.length === 0
                    ? "You haven't suggested any student work yet"
                    : "No suggestions match this filter"}
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  {suggestions.length === 0
                    ? "Use the + button on the Wall of Fame to suggest a student's work."
                    : "Try another status or clear the search."}
                </p>
              </div>
            ) : (
              <ul className="space-y-3" aria-label="Your suggestions">
                {visible.map((s) => {
                  const kind = kindOf(s);
                  const pin = pinState(s);
                  return (
                    <li
                      key={s._id}
                      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                        <KindThumb suggestion={s} />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <h2 className="truncate text-base font-bold text-slate-800">
                                {s.title || "Untitled"}
                              </h2>
                              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600">
                                <span className="inline-flex items-center gap-1">
                                  <kind.icon
                                    className="h-3.5 w-3.5 text-slate-400"
                                    aria-hidden="true"
                                  />
                                  {kind.label}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                  <UserRound
                                    className="h-3.5 w-3.5 text-slate-400"
                                    aria-hidden="true"
                                  />
                                  {s.studentName}
                                  <span className="text-slate-400">·</span>
                                  {s.balagruha}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                  <CalendarDays
                                    className="h-3.5 w-3.5 text-slate-400"
                                    aria-hidden="true"
                                  />
                                  Suggested {formatDate(s.suggestedAt)}
                                </span>
                              </div>
                            </div>
                            <StatusChip status={s.status} />
                          </div>

                          {s.reason && (
                            <p className="mt-2 line-clamp-2 text-sm italic text-slate-500">
                              “{s.reason}”
                            </p>
                          )}

                          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5">
                            <div className="min-w-0 text-sm">
                              <div className="font-semibold text-slate-700">
                                {reviewOutcome(s)}
                              </div>
                              {s.status !== "pending" && s.reviewNotes && (
                                <div className="mt-0.5 line-clamp-2 text-slate-600">
                                  Admin note: “{s.reviewNotes}”
                                </div>
                              )}
                              {pin && (
                                <div
                                  className={`mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-medium ${
                                    pin.live
                                      ? "text-emerald-700"
                                      : "text-slate-500"
                                  }`}
                                >
                                  <span className="inline-flex items-center gap-1">
                                    <Pin
                                      className="h-3.5 w-3.5"
                                      aria-hidden="true"
                                    />
                                    {pin.text}
                                  </span>
                                  {s.pin && (
                                    <Engagement engagement={s.pin.engagement} />
                                  )}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setSelected(s)}
                              aria-label={`View details for ${s.title || "suggestion"}`}
                              className="inline-flex flex-none items-center gap-1.5 rounded-full border border-violet-200 bg-white px-3.5 py-1.5 text-sm font-semibold text-violet-700 hover:bg-violet-50"
                            >
                              <Eye className="h-4 w-4" aria-hidden="true" />
                              View
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>

      <SuggestionDetails
        suggestion={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

export default CoachSuggestionsManagement;
